import { useState } from 'react';
import { useJournal } from '../api/hooks';
import type { JournalEntry, JournalFilters } from '../api/types';
import { formatDate } from '../lib/format';
import { EmptyState, ErrorBox, ROW, Spinner, TABLE, TD, TH, THEAD_ROW } from './ui';

function statusTone(status: number): string {
  if (status >= 500) return 'bg-error text-white';
  if (status >= 400) return 'bg-jupiter text-bg';
  if (status >= 300) return 'bg-neptune text-white';
  return 'bg-success text-white';
}

function Row({ entry }: { entry: JournalEntry }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className={`${ROW} align-top`}>
        <td className={`${TD} whitespace-nowrap font-mono text-xs text-outline`}>{formatDate(entry.ts)}</td>
        <td className={`${TD} text-xs font-semibold`}>{entry.method}</td>
        <td className={`${TD} text-xs`}>{entry.endpoint ?? entry.path}</td>
        <td className={TD}>
          <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${statusTone(entry.status)}`}>
            {entry.status}
          </span>
        </td>
        <td className={`${TD} font-mono text-[11px] text-outline`}>{entry.guid ?? '—'}</td>
        <td className={TD}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs font-semibold text-neptune hover:underline"
            aria-expanded={open}
          >
            {open ? 'Replier' : 'Détails'}
          </button>
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-line bg-hover">
          <td colSpan={6} className="px-3 py-2">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-outline">En-têtes</p>
                <pre className="mt-1 max-h-48 overflow-auto rounded-lg border border-line bg-bg p-2 font-mono text-xs">
                  {JSON.stringify(entry.headers, null, 2)}
                </pre>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-outline">Corps / query</p>
                <pre className="mt-1 max-h-48 overflow-auto rounded-lg border border-line bg-bg p-2 font-mono text-xs">
                  {JSON.stringify({ query: entry.query, body: entry.body, note: entry.note }, null, 2)}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** Live journal (polled every 2 s), reused by /journal and by the order page. */
export default function JournalTable({ filters }: { filters?: JournalFilters }) {
  const { data, isPending, error } = useJournal(filters ?? {}, { refetchInterval: 2000 });

  if (error) return <ErrorBox error={error} />;
  if (isPending) return <Spinner />;
  if (!data?.length) return <EmptyState>Aucun appel enregistré.</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className={`${TABLE} min-w-[720px]`}>
        <thead>
          <tr className={THEAD_ROW}>
            <th className={TH}>Horodatage</th>
            <th className={TH}>Méthode</th>
            <th className={TH}>Endpoint</th>
            <th className={TH}>Statut</th>
            <th className={TH}>Guid</th>
            <th className={TH} />
          </tr>
        </thead>
        <tbody>
          {data.map((entry) => (
            <Row key={entry.id} entry={entry} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
